import React from 'react';
import { BarChart3, CheckCircle2, XCircle } from 'lucide-react';

const BAR_COLORS = ['#3a624e', '#a67c3d', '#5a855a', '#8a624c'];

/** Shared ERP analytics card — pipeline, ranked, status (donut), default bars */
export default function AnalyticsChartCard({ chart, empty, qawaem }) {
  const rows = chart.rows || [];
  const maxVal = Math.max(...rows.map((r) => Number(r.value) || 0), 1);
  const total = rows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);
  const variant = chart.variant || 'default';

  const panelClass = qawaem
    ? `qawaem-dash-panel ${variant === 'pipeline' ? '' : variant === 'ranked' ? '' : 'glass-chart-green'}`
    : 'bg-white/10 backdrop-blur-lg rounded-3xl p-5 border border-white/20';

  const titleClass = qawaem ? 'qawaem-dash-panel-title' : 'text-lg font-semibold text-white';
  const emptyClass = qawaem ? 'text-console-muted text-sm' : 'text-white/50 text-sm';

  if (variant === 'status') {
    const active = Number(chart.active) || 0;
    const inactive = Number(chart.inactive) || 0;
    const statusTotal = active + inactive;
    const activePct = statusTotal > 0 ? Math.round((active / statusTotal) * 100) : 0;

    return (
      <div className={panelClass}>
        <div className={qawaem ? 'qawaem-dash-panel-head' : 'mb-4'}>
          <div>
            <h2 className={titleClass}>{chart.title}</h2>
            {chart.subtitle && qawaem ? <p className="qawaem-dash-panel-sub">{chart.subtitle}</p> : null}
          </div>
        </div>
        {!statusTotal ? (
          <p className={emptyClass}>{empty}</p>
        ) : (
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {qawaem ? (
              <div
                className="qawaem-analytics-donut shrink-0"
                style={{
                  background: `conic-gradient(#3a624e 0 ${activePct}%, #d4cfc4 ${activePct}% 100%)`,
                }}
                aria-hidden
              >
                <div className="qawaem-analytics-donut-inner">
                  <span className="text-xl font-bold text-forest-900 tabular-nums">{activePct}%</span>
                  <span className="text-[10px] text-console-muted">نشط</span>
                </div>
              </div>
            ) : null}
            <div className="flex-1 w-full space-y-3">
              <div className={qawaem ? 'qawaem-analytics-status-row' : 'flex justify-between text-white/80'}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className={`h-4 w-4 ${qawaem ? 'text-[#3a624e]' : 'text-emerald-400'}`} />
                  <span className={`text-sm ${qawaem ? 'text-forest-900' : ''}`}>نشط</span>
                </div>
                <span className={`text-sm font-bold tabular-nums ${qawaem ? 'text-forest-900' : 'text-white'}`}>
                  {active}
                </span>
              </div>
              <div className={qawaem ? 'qawaem-analytics-status-row' : 'flex justify-between text-white/80'}>
                <div className="flex items-center gap-2">
                  <XCircle className={`h-4 w-4 ${qawaem ? 'text-[#a67c3d]' : 'text-rose-400'}`} />
                  <span className={`text-sm ${qawaem ? 'text-forest-900' : ''}`}>غير نشط</span>
                </div>
                <span className={`text-sm font-bold tabular-nums ${qawaem ? 'text-forest-900' : 'text-white'}`}>
                  {inactive}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={panelClass}>
      <div className={qawaem ? 'qawaem-dash-panel-head' : 'mb-4'}>
        <div>
          <h2 className={titleClass}>{chart.title}</h2>
          {chart.subtitle && qawaem ? <p className="qawaem-dash-panel-sub">{chart.subtitle}</p> : null}
        </div>
        {variant === 'pipeline' && qawaem && total > 0 ? (
          <div className="qawaem-analytics-pill">
            <BarChart3 className="h-4 w-4" />
            <span>{total}</span>
          </div>
        ) : null}
      </div>
      {!rows.length ? (
        <p className={emptyClass}>{empty}</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row, idx) => {
            const value = Number(row.value) || 0;
            const pct = Math.min(100, (value / maxVal) * 100);
            const share = total > 0 ? Math.round((value / total) * 100) : 0;
            const isTop = variant === 'ranked' && idx === 0;

            if (variant === 'pipeline' && qawaem) {
              return (
                <div key={row.label} className="qawaem-analytics-pipeline-row">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-sm font-medium text-forest-900 truncate">{row.label}</span>
                    <div className="flex items-center gap-2 shrink-0 tabular-nums">
                      <span className="text-xs font-semibold text-console-muted">{share}%</span>
                      <span className="text-sm font-bold text-forest-900">{row.display ?? row.value}</span>
                    </div>
                  </div>
                  <div className="qawaem-dash-bar-track">
                    <div
                      className="qawaem-dash-bar-fill"
                      style={{
                        width: `${Math.max(share, value > 0 ? 4 : 0)}%`,
                        background: BAR_COLORS[idx % BAR_COLORS.length],
                      }}
                    />
                  </div>
                </div>
              );
            }

            if (variant === 'ranked' && qawaem) {
              return (
                <div
                  key={row.label}
                  className={`qawaem-analytics-ranked-row ${isTop ? 'qawaem-analytics-ranked-row--top' : ''}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-sm font-medium truncate">{row.label}</span>
                    <span className="text-sm font-bold tabular-nums shrink-0">{row.display ?? row.value}</span>
                  </div>
                  <div className="qawaem-dash-bar-track qawaem-dash-bar-track--thin">
                    <div
                      className="qawaem-dash-bar-fill"
                      style={{
                        width: `${Math.max(4, pct)}%`,
                        background: isTop ? '#2e452e' : '#6e9b6e',
                      }}
                    />
                  </div>
                </div>
              );
            }

            return (
              <div key={row.label} className={qawaem ? '' : 'flex items-center gap-3'}>
                {qawaem ? (
                  <>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm text-console truncate" title={row.label}>
                        {row.label}
                      </span>
                      <span className="text-sm font-semibold text-forest-900 tabular-nums shrink-0">
                        {row.display ?? row.value}
                      </span>
                    </div>
                    <div className="qawaem-dash-bar-track">
                      <div
                        className="qawaem-dash-bar-fill"
                        style={{
                          width: `${pct}%`,
                          background: BAR_COLORS[idx % BAR_COLORS.length],
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-white/70 text-sm w-28 shrink-0 truncate" title={row.label}>
                      {row.label}
                    </span>
                    <div className="flex-1 bg-white/10 h-2 rounded-full overflow-hidden">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                          background: BAR_COLORS[idx % BAR_COLORS.length],
                        }}
                      />
                    </div>
                    <span className="text-white font-medium text-sm w-14 text-end tabular-nums">
                      {row.display ?? row.value}
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
